import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Trash2, Inbox, Star, BarChart3 } from "lucide-react";
import { UsageStats } from "@/components/UsageStats";
import { toast } from "sonner";
import { useAuth } from "@/context/Auth";
import { http, errMsg } from "@/lib/api";
import { Stars } from "@/components/CaseRating";

const FeedbackList = ({ items, onRemove }) => (
  <div className="mt-6 space-y-3" data-testid="admin-feedback-list">
    {!items ? <Loader2 className="animate-spin" /> : items.length === 0 ? (
      <p data-testid="admin-feedback-empty" className="text-sm" style={{ color: "var(--ink-3)" }}>No feedback yet.</p>
    ) : items.map((f) => (
      <div key={f.id} data-testid={`admin-feedback-item-${f.id}`} className="card p-5">
        <div className="flex justify-between gap-3 text-xs font-mono" style={{ color: "var(--ink-3)" }}>
          <span>{new Date(f.created_at).toLocaleString("en-AU")} · {f.user_email || "guest"}{f.contact ? ` · contact: ${f.contact}` : ""}{f.page ? ` · ${f.page}` : ""}</span>
          <button data-testid={`admin-feedback-delete-${f.id}`} onClick={() => onRemove(f.id)} aria-label="Delete" className="hover:text-[var(--alert)]"><Trash2 size={14} /></button>
        </div>
        <p className="mt-2 text-sm whitespace-pre-wrap">{f.text}</p>
      </div>
    ))}
  </div>
);

const RatingsPanel = ({ data }) => (
  <div className="mt-6 space-y-6" data-testid="admin-ratings-panel">
    {!data ? <Loader2 className="animate-spin" /> : data.cases.length === 0 ? (
      <p data-testid="admin-ratings-empty" className="text-sm" style={{ color: "var(--ink-3)" }}>No case ratings yet.</p>
    ) : (
      <>
        <div className="card divide-y" style={{ borderColor: "var(--line)" }}>
          <p className="px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--ink-3)" }}>By case (lowest first)</p>
          {data.cases.map((c) => (
            <div key={c.case_id} data-testid={`admin-rating-case-${c.case_id}`} className="px-5 py-3 flex items-center gap-3 text-sm" style={{ borderColor: "var(--line)" }}>
              <span className="font-mono text-xs w-10" style={{ color: "var(--ink-3)" }}>D{c.domain}</span>
              <span className="flex-1">{c.title}</span>
              <Stars value={c.avg_rating} />
              <span className="font-mono text-xs w-10 text-right" style={{ color: "var(--ink-3)" }}>×{c.rating_count}</span>
            </div>
          ))}
        </div>
        <div className="card divide-y" style={{ borderColor: "var(--line)" }} data-testid="admin-ratings-recent">
          <p className="px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--ink-3)" }}>Recent ratings</p>
          {data.recent.map((r, i) => (
            <div key={i} className="px-5 py-2.5 flex items-center gap-3 text-xs" style={{ borderColor: "var(--line)" }}>
              <span className="font-mono w-36 shrink-0" style={{ color: "var(--ink-3)" }}>{new Date(r.updated_at).toLocaleString("en-AU")}</span>
              <span className="flex-1 truncate">{r.case_title}</span>
              <span className="truncate max-w-[160px]" style={{ color: "var(--ink-3)" }}>{r.rater_email || "guest"}</span>
              <Stars value={r.rating} />
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [tab, setTab] = useState("usage");

  useEffect(() => {
    if (user?.role !== "admin") return;
    http.get("/admin/feedback").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
    http.get("/admin/ratings").then((r) => setRatings(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [user]);

  if (user === null) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/account" replace />;

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await http.delete(`/admin/feedback/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tabs = [["usage", "Usage", BarChart3, null], ["feedback", "Feedback", Inbox, items?.length], ["ratings", "Case ratings", Star, ratings?.recent.length]];
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-2xl sm:text-3xl font-bold">Owner inbox</h1>
      <p className="text-sm mt-1" style={{ color: "var(--ink-3)" }}>Only visible to the owner account.</p>
      <div className="mt-6 inline-flex p-1 rounded-full" style={{ background: "var(--muted-bg)" }}>
        {tabs.map(([k, label, Icon, n]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className="px-4 h-9 rounded-full text-sm font-medium flex items-center gap-1.5"
            style={tab === k ? { background: "var(--surface)", boxShadow: "0 1px 3px rgba(0,0,0,.12)" } : { color: "var(--ink-3)" }}>
            <Icon size={14} /> {label} {n != null && <span className="font-mono text-xs">({n})</span>}
          </button>
        ))}
      </div>
      {tab === "usage" ? <UsageStats /> : tab === "feedback" ? <FeedbackList items={items} onRemove={remove} /> : <RatingsPanel data={ratings} />}
    </div>
  );
}
